import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { Form, Formik } from 'formik';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import * as Yup from 'yup';
import CardPaymentFormFields from 'react/shared/card/components/CardPaymentFormFields';
import PopUp from 'react/shared/components/popups/PopUp';
import StateDropdown from 'react/shared/components/state_dropdown';
import TrueLinkBanner from 'react/shared/components/true_link/lab/TrueLinkBanner';
import TrueLinkButton from 'react/shared/components/true_link/main/TrueLinkButton';
import CountryOfCitizenship from 'react/shared/components/true_link/main/form/country_of_citizenship/CountryOfCitizenship';
import TrueLinkRadios from 'react/shared/components/true_link/main/form/radios/TrueLinkRadios';
import TrueLinkSelect from 'react/shared/components/true_link/main/form/select/TrueLinkSelect';
import addressShape from 'react/shared/shapes/address_shape';
import PALETTE from 'react/shared/theme/palette';
import { capitalize, humanize } from 'react/shared/utils/Strings';
import schema from 'react/shared/utils/schemas';
import ErrorText from 'react/signups/card/common/ErrorText';
import { yesNoToBoolean } from 'react/signups/card/utils';

export default function ReplacementCardModal({
  accountSlug,
  allowAutomatedPayment,
  cardholderAddress,
  adminMobile,
  cardholderMobile,
  cardholderName,
  cardholderCountryOfCitizenship,
  disableSelectingLastKnownAddress,
  forExpiringCard,
  isStableOrAble,
  onClose,
  onSuccess,
  organizationAddress,
  otherShippingDestinationDefault,
  paymentMethods,
  shippingAddress,
  shippingFee,
  shippingSettings,
}) {
  const [possiblePoBox, setPossiblePoBox] = useState(false);
  const [shippingInfo, setShippingInfo] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const countryOfCitizenshipLabel = 'Cardholder Country of Citizenship';
  const nonResidentAlienLabel = `Is the Cardholder a non-resident alien?`;
  const currentAddressOption = {
    id: 'ship_to_current_shipping',
    label: (
      <>
        <Typography sx={{ mr: 1 }} variant="body2">
          Last known shipping address
          <span style={{ color: '#757575' }}> - {shippingAddress || 'address not provided'}</span>
        </Typography>
      </>
    ),
    value: 'current_shipping_address',
  };

  const initShippingDestinationOptions = [
    {
      id: 'ship_to_home',
      label: (
        <Typography sx={{ mr: 1 }} variant="body2">
          Cardholder home
          <span style={{ color: '#757575' }}> - {cardholderAddress || 'address not provided'}</span>
        </Typography>
      ),
      value: 'cardholder_home',
      disabled: !cardholderAddress,
    },
    {
      id: 'ship_to_office',
      label: (
        <>
          <Typography sx={{ mr: 1 }} variant="body2">
            Office
            <span style={{ color: '#757575' }}>
              - {organizationAddress || 'address not provided'}
            </span>
          </Typography>
        </>
      ),
      value: 'office',
      disabled: !organizationAddress,
    },
    {
      id: 'ship_to_other',
      label: <Typography variant="body2">Other</Typography>,
      value: 'other',
    },
  ];

  const shippingDestinationOptions =
    shippingAddress && !disableSelectingLastKnownAddress
      ? [
          ...initShippingDestinationOptions.slice(0, 2),
          currentAddressOption,
          ...initShippingDestinationOptions.slice(2),
        ]
      : initShippingDestinationOptions;

  const name = () => cardholderName || 'the Cardholder';

  const poBoxWarning = () => {
    if (possiblePoBox) {
      return 'The address you requested looks like a PO Box. Unfortunately we are prohibited from shipping cards to PO Boxes. If this address is not a PO Box, please press submit. If it is a PO Box, please press cancel and request a new address. If you continue with this request using a PO Box address, the card order will be delayed and a new shipping address will be requested.';
    }
  };

  const checkAddresses = (event) => {
    const $target = $(event.target);
    const street1 = $target
      .closest('form')
      .find("input[name='card_reissue[address_attributes][street1]']")
      .val();
    const street2 = $target
      .closest('form')
      .find("input[name='card_reissue[address_attributes][street2]']")
      .val();
    const regexPoBox = new RegExp($tlf.domains.poBoxRegex, 'gmi');

    if (regexPoBox.test(street1) || regexPoBox.test(street2)) {
      setPossiblePoBox(true);
    } else {
      setPossiblePoBox(false);
    }
  };

  const tlExpiringCardHelpUrl =
    'https://help.admin.truelinkfinancial.com/hc/en-us/articles/27704688578715-Upcoming-Card-Expiration';

  const ableExpiringCardHelpUrl =
    'https://help.able.truelinkfinancial.com/hc/en-us/articles/31773881884955-Upcoming-ABLE-Card-Expiration';

  const tlReplacementCardHelpUrl =
    'https://help.admin.truelinkfinancial.com/hc/en-us/articles/27686180941851-Ordering-a-replacement-Card';
  const expiringCardHelpUrl = isStableOrAble ? ableExpiringCardHelpUrl : tlExpiringCardHelpUrl;

  const lostOrStolenSubheader = () => (
    <>
      <b>We will close the card immediately and mail a new one.</b>
      When the new card is activated, the funds will be moved to the new card.
      <a href={tlReplacementCardHelpUrl} rel="noreferrer" target="_blank">
        Have questions? Check out this Help Center article.
      </a>
    </>
  );

  const neverReceivedSubheader = () => (
    <>
      <p>Please check the tracking number on your account tab if the order was expedited.</p>
      <p>
        We recommend waiting at least 10 business days days before replacing if ordered standard
        since postal delivery can be delayed.
      </p>
    </>
  );

  const damagedSubheader = () => (
    <>
      <b>We will keep the current card open, while we mail the new one.</b>
      When the new card is activated, the current card will be closed automatically, and the funds
      will be moved over to the new card.
      <a href={tlReplacementCardHelpUrl} rel="noreferrer" target="_blank">
        Have questions? Check out this Help Center article.
      </a>
    </>
  );

  const expiringSubheader = () => (
    <>
      <b>
        {isStableOrAble
          ? 'The current card will be closed immediately when the replacement card is ordered. Once the replacement card arrives and is activated, the card balance will be accessible again. '
          : 'The current card will remain open until you activate the replacement card or the current card expires. '}
      </b>
      <a href={expiringCardHelpUrl} rel="noreferrer" target="_blank">
        Learn more about replacing an expiring card.
      </a>
    </>
  );

  const cardReplacementSubheader = (formik) => {
    if (forExpiringCard) {
      return expiringSubheader();
    }

    switch (formik.values.replacement_card_reason) {
      case 'never_received_card':
        return neverReceivedSubheader();
      case 'damaged_card':
        return damagedSubheader();
      case 'lost_stolen_compromised_card':
        return lostOrStolenSubheader();
      default:
        return lostOrStolenSubheader();
    }
  };

  const subheader = (formik) => (
    <Box sx={{ gap: 2, display: 'flex', flexDirection: 'column', fontSize: 18 }}>
      {cardReplacementSubheader(formik)}
    </Box>
  );

  const otherShippingDestinationInputs = (formik) => {
    const values = formik.values;
    if (values.shipping_destination === 'other') {
      return (
        <div>
          <div className="flex">
            <div>
              <label htmlFor="card_reissue_address_street1">Street</label>
              <input
                disabled={values.shipping_destination != 'other'}
                id="card_reissue_address_street1"
                name="address_attributes[street1]"
                onBlur={checkAddresses}
                onChange={formik.handleChange}
                style={{ height: 37 }}
                type="text"
                value={values.address_attributes.street1}
              />
              <ErrorText name="address_attributes[street1]" />
            </div>
            <div style={{ marginLeft: 10 }}>
              <label htmlFor="card_reissue_address_street2">Apt or unit number</label>
              <input
                disabled={values.shipping_destination != 'other'}
                id="card_reissue_address_street2"
                name="address_attributes[street2]"
                onBlur={checkAddresses}
                onChange={formik.handleChange}
                style={{ height: 37 }}
                type="text"
                value={values.address_attributes.street2}
              />
            </div>
          </div>
          <div className="flex">
            <span style={{ color: 'red' }}> {poBoxWarning()} </span>
          </div>
          <div className="flex">
            <div>
              <label htmlFor="card_reissue_address_city">City</label>
              <input
                id="card_reissue_address_city"
                name="address_attributes[city]"
                onChange={formik.handleChange}
                style={{ height: 37 }}
                type="text"
                value={values.address_attributes.city}
              />
              <ErrorText name="address_attributes[city]" />
            </div>
            <div style={{ marginLeft: 10 }}>
              <label htmlFor="payee_state">State</label>
              <StateDropdown
                id="payee_state"
                name="address_attributes[state]"
                onChange={formik.handleChange}
                value={values.address_attributes.state}
              />
              <ErrorText name="address_attributes[state]" />
            </div>
            <div style={{ marginLeft: 10 }}>
              <label htmlFor="card_reissue_address_zip">Zip</label>
              <input
                id="card_reissue_address_zip"
                maxLength="5"
                name="address_attributes[zip]"
                onChange={formik.handleChange}
                placeholder="Zip code"
                style={{ width: 70, height: 37 }}
                type="text"
                value={values.address_attributes.zip}
              />
              <ErrorText name="address_attributes[zip]" />
            </div>
          </div>
          <br />
        </div>
      );
    }
  };

  const requestCardReissue = (formikValues) => {
    const filteredData = {};
    const isExpeditedShipping = formikValues.expedite_shipping === 'yes';

    for (const key in formikValues) {
      if (
        key !== 'phone_type' &&
        key !== 'countryOfCitizenship' &&
        key !== 'nonResidentAlien' &&
        formikValues[key]
      ) {
        filteredData[key] = formikValues[key];
      }
    }

    if (filteredData['expedite_shipping'] === 'no') {
      delete filteredData['card_recipient_phone'];
    }

    delete filteredData['expedite_charged'];
    delete filteredData['replacement_card_reason'];
    const cardholder = {
      country_of_citizenship: formikValues.countryOfCitizenship,
      non_resident_alien: yesNoToBoolean(formikValues.nonResidentAlien),
    };

    const { expedited_shipping_card_fee, ...reissue_fields } = filteredData;
    const data = {
      cardholder,
      ...(allowAutomatedPayment && isExpeditedShipping ? { expedited_shipping_card_fee } : {}),
      card_reissue: {
        ...reissue_fields,
      },
      for_expiring_card: forExpiringCard,
      replacement_card_reason: formikValues.replacement_card_reason,
    };
    const params = { account_id: accountSlug, format: 'json' };

    $.post(RailsRoutes.reissue_card_dashboard_cards_path(params), data)
      .done((data) => {
        setShippingInfo(data.shipping_information);
        setShowSuccessModal(true);
      })
      .fail((error) => {
        let output = '';
        error.responseJSON.forEach((message) => {
          output += ` ${message} \n`;
        });
        Truelink.flash('error', output);
      });
  };

  const validationSchema = Yup.object().shape({
    shipping_destination: Yup.string()
      .label(
        `You must select where to send ${name()}'s True Link Card before requesting a reissue.`,
      )
      .required(),
    countryOfCitizenship: !cardholderCountryOfCitizenship
      ? schema.countryOfCitizenship.required()
      : schema.countryOfCitizenship,
    nonResidentAlien: Yup.string().when(['countryOfCitizenship'], {
      is: (countryOfCitizenship) => countryOfCitizenship !== 'US',
      then: () => schema.nonResidentAlien.required(),
      otherwise: () => schema.nonResidentAlien.nullable(),
    }),
    expedite_shipping: Yup.string().oneOf(['yes', 'no']).required(),
    expedited_shipping_card_fee: Yup.object().shape({
      payment_method: Yup.string(),
      bank_account_id: Yup.string().when('payment_method', {
        is: 'ach',
        then: () => Yup.string().required('field required'),
        otherwise: () => Yup.string().nullable(),
      }),
    }),
    phone_type: Yup.string().when(['expedite_shipping'], {
      is: (expediteShipping) => expediteShipping === 'yes' || expediteShipping === true,
      then: () => Yup.string().required('field required'),
      otherwise: () => Yup.string(),
    }),
    card_recipient_phone: Yup.string().when(['expedite_shipping', 'phone_type'], {
      is: (expediteShipping, phoneType) =>
        (expediteShipping === 'yes' || expediteShipping === true) && phoneType === 'other',
      then: () => Yup.string().required('field required'),
      otherwise: () => Yup.string(),
    }),
    address_attributes: Yup.object().when(['shipping_destination'], {
      is: (shipping_destination) => shipping_destination == 'other',
      then: () => schema.address,
      otherwise: () => Yup.object(),
    }),
  });

  const getAddress = (address) => ({
    street1: address?.street1 || '',
    street2: address?.street2 || '',
    city: address?.city || '',
    state: address?.state || '',
    zip: address?.zip || '',
  });

  const replacementCardReasonOptions = [
    {
      id: 'lost_stolen_compromised_card',
      label: `The card is lost, stolen, or compromised`,
      value: 'lost_stolen_compromised_card',
    },
    {
      id: 'damaged_card',
      label: 'The card has been damaged',
      value: 'damaged_card',
    },
    {
      id: 'never_received_card',
      label: 'I never received my card in the mail',
      value: 'never_received_card',
    },
  ];

  const replacementCardReason = (formik) => (
    <Box sx={{ mb: '10px' }}>
      <Typography variant="h6">Why are you ordering a replacement card?</Typography>
      <TrueLinkRadios
        ariaLabel="replacement card reason"
        leftAligned
        name="replacement_card_reason"
        onChange={formik.handleChange}
        options={replacementCardReasonOptions}
        size="small"
        value={formik.values.replacement_card_reason}
      />
    </Box>
  );

  const initialValues = {
    shipping_destination: '',
    countryOfCitizenship: cardholderCountryOfCitizenship || '',
    nonResidentAlien: '',
    expedited_shipping_card_fee: {
      payment_method:
        paymentMethods?.available_bank_accounts?.length > 0 &&
        paymentMethods?.preferred_payment_method
          ? paymentMethods?.preferred_payment_method
          : 'card',
      bank_account_id: paymentMethods?.preferred_bank_account?.id || '',
    },
    expedite_shipping: 'no',
    expedite_charged: true,
    phone_type: '',
    card_recipient_phone: '',
    address_attributes: getAddress(otherShippingDestinationDefault || {}),
    replacement_card_reason: 'lost_stolen_compromised_card',
  };

  const renderForm = (formik) => (
    <div style={{ fontSize: `18px !important` }}>
      <Box sx={{ mb: 4 }}>
        The replacement card will have a new card number, expiration date, and CVV code.
      </Box>
      {$tlf.flipperShowReplacementCardReason &&
        !forExpiringCard &&
        !isStableOrAble &&
        replacementCardReason(formik)}
      <TrueLinkBanner content={subheader(formik)} variant="info" />

      <Form className="card_reissue_form" id="card-reissue-form">
        <br />
        <Typography sx={{ mb: '10px' }} variant="h6">
          Where should we send {name()}'s new True Link Card?
        </Typography>
        <TrueLinkSelect
          id="shipping-destination"
          label="Which shipping address would you prefer"
          name="shipping_destination"
          onChange={formik.handleChange}
          options={shippingDestinationOptions}
          placeholder={'Select Address'}
          required
          setFieldTouched={formik.setFieldTouched}
          style={{ display: 'flex' }}
          value={formik.values.shipping_destination}
        />
        <Box sx={{ mt: 3, ml: 2 }}>{otherShippingDestinationInputs(formik)}</Box>
        <CardPaymentFormFields
          adminMobile={adminMobile}
          allowAutomatedPayment={allowAutomatedPayment}
          cardholderMobile={cardholderMobile}
          expeditedShipping={shippingSettings.expedited}
          handleChange={formik.handleChange}
          paymentMethods={paymentMethods}
          regularShipping={shippingSettings.regular}
          setFieldTouched={formik.setFieldTouched}
          setFieldValue={formik.setFieldValue}
          shippingFee={shippingFee}
          values={formik.values}
        />
        {!cardholderCountryOfCitizenship && (
          <Box sx={{ mt: 3 }}>
            <CountryOfCitizenship
              boldLabel
              compactUI
              countryOfCitizenshipLabel={countryOfCitizenshipLabel}
              nonResidentAlienLabel={nonResidentAlienLabel}
              onChange={formik.handleChange}
              setFieldTouched={formik.setFieldTouched}
              values={formik.values}
            />
          </Box>
        )}
      </Form>
    </div>
  );

  const getShippingInfo = (formikValues) =>
    `${formikValues.expedite_shipping === 'yes' ? 'Expedited' : 'Standard'} - ${shippingInfo.range[0]} to ${shippingInfo.range[1]} business days.`;

  const getAddressInfo = (formikValues) => {
    const address = formikValues.address_attributes;
    const addressInfo = `${address.street1} ${address.street2}, ${address.city}, ${address.state} ${address.zip}`;
    const addressType = capitalize(humanize(formikValues.shipping_destination));
    return `${addressType} - ${addressInfo}`;
  };

  const getRow = (label, value) => (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Typography sx={{ color: PALETTE.grey3, mb: 1 }} variant="body1">
        {label}
      </Typography>
      <Typography variant="body1">{value}</Typography>
      <Divider component="div" sx={{ my: 2 }} />
    </Box>
  );

  const renderSuccess = (formik) => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <CheckCircleIcon color="primary" fontSize="large" />
      <Typography sx={{ my: 1 }} variant="h3">
        Success!
      </Typography>
      <Typography variant="body2">
        You’ve ordered a replacement personalized card for {name()}.
      </Typography>
      <Divider component="div" sx={{ my: 2 }} />
      {getRow('Address', getAddressInfo(formik.values))}
      {getRow('Shipping', getShippingInfo(formik.values))}
      {getRow('Estimated arrival', shippingInfo.arrival_date)}
    </Box>
  );

  const footer = (formik, showSuccessModal) => (
    <TrueLinkButton
      className="btn normal"
      onClick={showSuccessModal ? onSuccess : formik.handleSubmit}
      variant="none"
    >
      {showSuccessModal ? 'Done' : 'REQUEST REISSUE'}
    </TrueLinkButton>
  );

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={requestCardReissue}
      validationSchema={validationSchema}
    >
      {(formik) => (
        <PopUp
          bodyHeight="48vh"
          footer={footer(formik, showSuccessModal)}
          header={'Order a replacement card'}
          hideHeader={showSuccessModal}
          maxHeight="800px"
          maxWidth="630px"
          onClose={onClose}
          openModal
        >
          {showSuccessModal ? renderSuccess(formik) : renderForm(formik)}
        </PopUp>
      )}
    </Formik>
  );
}

ReplacementCardModal.propTypes = {
  accountSlug: PropTypes.string.isRequired,
  allowAutomatedPayment: PropTypes.bool.isRequired,
  cardholderAddress: PropTypes.string,
  adminMobile: PropTypes.string,
  cardholderMobile: PropTypes.string,
  cardholderCountryOfCitizenship: PropTypes.string,
  cardholderName: PropTypes.string,
  disableSelectingLastKnownAddress: PropTypes.bool,
  forExpiringCard: PropTypes.bool,
  isStableOrAble: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  organizationAddress: PropTypes.string.isRequired,
  otherShippingDestinationDefault: addressShape,
  paymentMethods: PropTypes.object,
  shippingAddress: PropTypes.string.isRequired,
  shippingFee: PropTypes.string,
  shippingSettings: PropTypes.object,
};

ReplacementCardModal.defaultProps = {
  forExpiringCard: false,
  isStableOrAble: false,
};
